import Sidebar from "@/components/Sidebar";
import NoticePopup from "@/components/NoticePopup";
import { Outlet, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useState, useEffect } from "react";
import { Menu, X, KeyRound, ChevronDown } from "lucide-react";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";

const TITLES = {
  "/": "Dashboard",
  "/customers": "Customer List",
  "/agents": "Agent List",
  "/services": "Service List",
  "/receipts": "Receipts",
  "/daily-assigned-service": "Daily Assigned Service",
  "/daily-login-service": "Daily Login Service",
  "/daily-status": "Daily Status",
  "/mis-report": "MIS Report",
  "/reports": "Reports",
  "/payment-types": "Payment Type",
  "/branches": "Branches",
  "/company": "Company Info",
  "/users": "Staff / Users",
  "/role-permissions": "Role Permissions",
};

export default function Layout() {
  const { user } = useAuth();
  const loc = useLocation();
  const title = TITLES[loc.pathname] || "";
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  // Close drawer + menu on route change
  useEffect(() => { setMobileOpen(false); setMenuOpen(false); }, [loc.pathname]);

  return (
    <div className="min-h-screen flex bg-[color:var(--canvas)] relative">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-backdrop"
        />
      )}
      {/* Sidebar: off-canvas drawer on mobile, sticky column on desktop */}
      <div
        className={`fixed inset-y-0 left-0 z-50 md:z-0 md:sticky md:top-0 transform transition-transform duration-200 ease-out ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0`}
      >
        <Sidebar onNavClick={() => setMobileOpen(false)}>
          {/* Close button visible only on mobile */}
          <button
            onClick={() => setMobileOpen(false)}
            className="md:hidden absolute top-3 right-3 p-1.5 rounded-md bg-white/10 text-white/80 hover:bg-white/20"
            data-testid="mobile-sidebar-close"
            aria-label="Close menu"
          >
            <X className="h-4 w-4" />
          </button>
        </Sidebar>
      </div>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 sticky top-0 z-30 bg-white border-b border-slate-200 flex items-center px-3 sm:px-6 gap-3 sm:gap-6">
          <button
            onClick={() => setMobileOpen(true)}
            className="md:hidden p-2 -ml-1 rounded-md hover:bg-slate-100 text-slate-700"
            aria-label="Open menu"
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="font-display font-bold text-[13px] sm:text-[15px] tracking-tight text-slate-900 truncate">
            <span className="hidden sm:inline">SHREE SAMARTH CONSULTANCY</span>
            <span className="sm:hidden">SHREE SAMARTH</span>
          </div>
          <div className="flex-1" />
          <div className="text-xs text-slate-500 hidden lg:block">
            {new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short", year: "numeric" })}
          </div>

          {/* User menu with change password */}
          <div className="relative">
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="flex items-center gap-2 pl-3 sm:pl-6 sm:border-l border-slate-200 text-xs font-semibold text-slate-700 hover:text-slate-900"
              data-testid="user-menu-btn"
            >
              <span className="h-7 w-7 rounded-full bg-[color:var(--brand-teal)]/10 text-[color:var(--brand-teal)] flex items-center justify-center font-bold uppercase text-[11px]">
                {(user?.name || user?.email || "U")[0]}
              </span>
              <span className="hidden sm:inline max-w-[180px] truncate">{user?.email}</span>
              <ChevronDown className="h-3.5 w-3.5 opacity-60" />
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                <div
                  className="absolute right-0 mt-2 w-56 bg-white border border-slate-200 rounded-md shadow-lg overflow-hidden z-50"
                  data-testid="user-menu-dropdown"
                >
                  <div className="px-3 py-2 border-b border-slate-100">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500">Signed in as</div>
                    <div className="text-sm font-semibold text-slate-800 truncate">{user?.name || "User"}</div>
                    <div className="text-[11px] text-slate-500 truncate">{user?.email}</div>
                  </div>
                  <button
                    onClick={() => { setMenuOpen(false); setPwOpen(true); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 text-left"
                    data-testid="menu-change-password"
                  >
                    <KeyRound className="h-4 w-4 text-[color:var(--brand-teal)]" /> Change Password
                  </button>
                </div>
              </>
            )}
          </div>
        </header>

        <div className="px-3 sm:px-6 pt-4 sm:pt-6 pb-2 flex items-baseline justify-between gap-3">
          <h1
            className="font-display font-black text-xl sm:text-3xl tracking-tight text-slate-900 truncate"
            data-testid="page-title"
          >
            {title}
          </h1>
          <div className="text-[11px] sm:text-xs text-slate-500 font-mono-num hidden sm:block whitespace-nowrap">
            Home <span className="mx-1">/</span> <span className="text-slate-700">{title}</span>
          </div>
        </div>

        <main className="flex-1 px-3 sm:px-6 py-3 sm:py-4 min-w-0">
          <Outlet />
        </main>
        <Toaster richColors position="top-right" />
      </div>

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      <NoticePopup />
    </div>
  );
}
